import type { T } from '../../i18n/index.js';
/**
 * Presentation formatters for the screen layer.
 *
 * Chip counts are integers and never carry a symbol or a decimal — design
 * spec §2 / domain.ts. There is deliberately no Intl.NumberFormat currency
 * path in this file, so no future caller can accidentally reach one.
 */

export function chips(n: number): string {
  return Math.trunc(n).toLocaleString('en-US');
}

/** Ledger and settlement lines: the sign is the whole story, so never drop it. */
export function signedChips(n: number): string {
  if (n === 0) return '0';
  return `${n > 0 ? '+' : '−'}${chips(Math.abs(n))}`;
}

/** Buy-in band as shown on a stake tier row. */
export function band(min: number, max: number): string {
  return `${chips(min)}–${chips(max)}`;
}

/** Basis points → the percentage players actually read ("5%"). */
export function bps(v: number): string {
  const pct = v / 100;
  return `${Number.isInteger(pct) ? pct : pct.toFixed(1)}%`;
}

/**
 * Honest queue ETA. The server sends its own estimate; when it has none we say
 * so instead of inventing a number — copy rule §12.
 *
 * Takes a translator rather than calling the hook: this module is imported by
 * non-components, and a number formatter has no business subscribing to
 * anything. The caller already has one.
 */
export function eta(ms: number, t: T): string {
  if (!Number.isFinite(ms) || ms <= 0) return t('fmt.eta.unknown');
  const s = Math.round(ms / 1000);
  if (s < 60) return t('fmt.eta.secs', { n: s });
  return t('fmt.eta.mins', { n: Math.round(s / 60) });
}

/** Ledger timestamps. Short, local, no seconds — the row is scanned, not read. */
export function whenShort(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Cool-off / self-exclusion countdowns, in the coarse unit that reads best. */
export function untilText(iso: string | null): string | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (!Number.isFinite(ms) || ms <= 0) return null;
  const hours = Math.ceil(ms / 3_600_000);
  if (hours < 48) return `${hours} oras pa`;
  return `${Math.ceil(hours / 24)} araw pa`;
}
